import AddToCartFormik from '@jetshop/core/components/Mutation/AddToCartFormik';
import t from '@jetshop/intl';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import { Form } from 'formik';
import React, { useEffect } from 'react';
import { styled } from 'linaria/react';
import addToCartMutation from '../../Cart/addToCart.gql';
import CartQuery from '../../Cart/CartQuery.gql';
import Button from '../../ui/Button';
import { useProductValidationMessage } from '../useProductValidationMessage';
import { VariantSelector } from '../VariantSelector';
import { theme } from '../../Theme';
import ProductToastWrapper from './ProductToast';
import { ConfigurationSelector } from '../ConfigurationSelector';
import { useAddedToCartUpdate } from '../../Cart/AddedToCartNotification';
import AddedToCartCard from '../../Cart/AddedToCartNotification/AddedToCartNotificationCard';

const StyledForm = styled(Form)`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-bottom: 1rem;
  ${theme.below.md} {
    align-items: center;
    margin-bottom: 0;
  }
`;

const QuantityAddToCartWrapper = styled('div')`
  margin-top: 24px;
  display: flex;
  width: 100%;
  align-items: flex-end;
`;

const AddToCartButton = styled(Button)`
  height: 40px;
  letter-spacing: 0.02em;
  width: 100%;
  color: ${theme.colors.white};
  background-color: ${theme.colors.black};
  :hover {
    background-color: ${theme.colors.text};
  }

  :disabled {
    background-color: ${theme.colors.text};
  }
`;

function AddToCartForm({
  product,
  variant,
  getMissingOptions,
  variantHandler,
  children
}) {
  const stockStatus = useStockStatus(variant || product);
  const outOfStockNotify = stockStatus.status === 'notifyWhenBack';
  const { validation: variantValidation, missingOptions } = variantHandler;
  const { hasVariants } = product;
  const { validationMessage, enableValidation } = useProductValidationMessage({
    missingOptions,
    variantValidation,
    hasVariants,
    stockStatus
  });

  const sizes = product.customFields.map(item => item);
  const sizesMap = sizes.map(item => item);
  const sizesRunObject = sizesMap.find(item => item.key === 'sizerun');
  const sizesRunValue = sizesRunObject ? sizesRunObject.stringValue : '';

  const mappedArray = product.categories.map(item => {
    return {
      primaryRoute: item.primaryRoute,
      typename: item.__typename
    };
  });

  const categories = mappedArray.map(item => {
    return item.primaryRoute.path;
  });

  const imageObject = product.images.map(item => {
    return item;
  });

  const newImageObject = imageObject.map(item => {
    return item.url;
  });

  const handleButtonClick = () => {
    // Only triggers when add to cart button is triggered
    const _learnq = window._learnq || [];
    const item = {
      AddedItemProductName: product.name,
      AddedItemProductID: product.articleNumber,
      AddedItemProductSKU: `${product.articleNumber}${sizesRunValue}`,
      AddedItemCategories: categories,
      AddedItemImageURL: newImageObject,
      AddedItemURL: product.primaryRoute.path,
      AddedItemPrice: product.price,
      AddedItemQuantity: 1,
      ItemNames: [product.name],
      Items: item?.map(() => ({
        ProductID: product.id,
        SKU: product.articleNumber,
        ProductName: product.name,
        Quantity: 1,
        ItemPrice: product.price,
        RowTotal: product.price,
        ProductURL: product.primaryRoute.path,
        ImageURL: newImageObject,
        ProductCategories: [categories]
      }))
      // Items: [
      //   {
      //     ProductID: product.id,
      //     SKU: product.articleNumber,
      //     ProductName: product.name,
      //     Quantity: 1,
      //     ItemPrice: product.price,
      //     RowTotal: product.price,
      //     ProductURL: product.primaryRoute.path,
      //     ImageURL: newImageObject,
      //     ProductCategories: [categories]
      //   }
      // ]
    };
    _learnq.push(['track', 'Added to Cart', item]);
  };

  useEffect(() => {
    const _learnq = window._learnq || [];
    const item = {
      ProductName: product.name,
      ProductID: product.articleNumber,
      SKU: `${product.articleNumber}${sizesRunValue}`,
      Categories: categories,
      ImageURL: newImageObject,
      URL: product.primaryRoute.path,
      Brand: product.name,
      Price: product.price,
      CompareAtPrice: product.previousPrice
    };
    _learnq.push(['track', 'Viewed Product', item]);
  }, []);

  // useEffect(() => {
  //   const _learnq = window._learnq || [];
  //   const item = {
  //     AddedItemProductName: product.name,
  //     AddedItemProductID: product.articleNumber,
  //     AddedItemProductSKU: product.articleNumber,
  //     AddedItemCategories: categories,
  //     AddedItemImageURL: newImageObject,
  //     AddedItemURL: product.primaryRoute.path,
  //     AddedItemPrice: product.price,
  //     AddedItemQuantity: 1,
  //     ItemNames: [product.name],
  //     Items: [
  //       {
  //         ProductID: product.id,
  //         SKU: product.articleNumber,
  //         ProductName: product.name,
  //         Quantity: 1,
  //         ItemPrice: product.price,
  //         RowTotal: product.price,
  //         ProductURL: product.primaryRoute.path,
  //         ImageURL: newImageObject,
  //         ProductCategories: [categories]
  //       }
  //     ]
  //   };
  //   _learnq.push(['track', 'Added to Cart', item]);
  // }, []);

  const handleClick = () => {
    enableValidation();
    handleButtonClick();
  };

  const { push, clear } = useAddedToCartUpdate();
  const exitDuration = 400;
  const onAddToCartInit = ({ mutationId, quantity, price }) => {
    push(
      <AddedToCartCard timeout={10000}>
        <ProductToastWrapper
          selectedVariation={variant}
          product={product}
          quantity={quantity}
          price={price}
          closeToast={_ => clear(mutationId, exitDuration)}
        />
      </AddedToCartCard>,
      { id: mutationId }
    );
  };
  const onAddToCartError = () => {
    return ({ selectedVariation, mutationId, quantity, price, error }) => {
      push(
        <AddedToCartCard timeout={10000}>
          <ProductToastWrapper
            selectedVariation={variant}
            product={product}
            quantity={quantity}
            price={price}
            closeToast={_ => clear(mutationId, exitDuration)}
          />
        </AddedToCartCard>,
        { id: mutationId }
      );
    };
  };
  return (
    <div>
      <AddToCartFormik
        onAddToCartInit={onAddToCartInit}
        onAddToCartError={onAddToCartError}
        cartQuery={CartQuery}
        cartMutation={addToCartMutation}
        product={product}
        variant={variant}
        getMissingOptions={getMissingOptions}
      >
        {() => {
          return (
            <>
              <VariantSelector
                className="variant-selector"
                product={product}
                variantHandler={variantHandler}
                showValidation={!!validationMessage}
              />
              {product.hasConfigurations && (
                <ConfigurationSelector product={product} />
              )}

              <StyledForm className="cta-button-wrapper">
                {!outOfStockNotify && (
                  <QuantityAddToCartWrapper className="quantity-add-to-cart-wrapper">
                    <AddToCartButton
                      data-testid="add-to-cart"
                      type="submit"
                      disabled={!!validationMessage}
                      onClick={handleClick}
                    >
                      {validationMessage ? validationMessage : t('Add to cart')}
                    </AddToCartButton>
                  </QuantityAddToCartWrapper>
                )}
              </StyledForm>
              {children}
            </>
          );
        }}
      </AddToCartFormik>
    </div>
  );
}

export default AddToCartForm;
